import React, { useMemo } from 'react';
import { IapScope, INCIDENT } from './ICSConstants';
import { useIncidentActions } from './hooks/incidentActionHooks';
import { useOrgActions } from './hooks/orgActionHooks';
import Loading from '../Loading/Loading';
import { Badge } from '@/components/ui/badge';
import {
  actionPriorityBadgeVariant,
  actionStatusBarClass,
} from './actionsTable/actionEditModel';
import {
  countOutsideWindow,
  describeHiddenActions,
  nowFraction,
  timelineBars,
  timelinePositionLanes,
  timelineWindow,
  type TimelineBar,
  type TimelineLanePosition,
  type TimelineRange,
  type TimelineRosterMember,
  type TimelineWindow,
} from './actionsTable/actionTimeline';
import { getActionMapId } from './utils/actionMapId';
import type { ActionEntity } from './hooks/useScopedActionForm';

type ActionsTimelineProps = {
  scope?: IapScope;
  incidentIdOverride?: string | null;
  range: TimelineRange;
  onEditAction: (action: ActionEntity) => void;
  filteredActions?: ActionEntity[];
  /** HWCG: one lane per roster position instead of one row per task. */
  showRosterPositions?: boolean;
  rosterPositions?: TimelineLanePosition[];
  roster?: TimelineRosterMember[];
};

const NO_POSITIONS: TimelineLanePosition[] = [];
const NO_ROSTER: TimelineRosterMember[] = [];

type BarRowProps = {
  bar: TimelineBar<ActionEntity>;
  window: TimelineWindow;
  nowAt: number | null;
  onEditAction: (action: ActionEntity) => void;
  idPrefix?: string;
};

const TimelineBarRow: React.FC<BarRowProps> = ({
  bar,
  window,
  nowAt,
  onEditAction,
  idPrefix = 'action-row',
}) => {
  const action = bar.action;
  return (
    <button
      type="button"
      id={`${idPrefix}-${getActionMapId(action)}`}
      className="tw:relative tw:flex tw:h-9 tw:w-full tw:items-center tw:border-0 tw:border-b tw:border-border tw:bg-transparent tw:px-0 tw:text-left"
      onClick={() => onEditAction(action)}
    >
      {window.ticks.map((tick) => (
        <span
          key={tick.at}
          className="tw:absolute tw:top-0 tw:h-full tw:w-px tw:bg-border/60"
          style={{ left: `${tick.fraction * 100}%` }}
        />
      ))}
      {nowAt != null ? (
        <span
          className="tw:absolute tw:top-0 tw:z-10 tw:h-full tw:w-px tw:bg-destructive/70"
          style={{ left: `${nowAt * 100}%` }}
        />
      ) : null}
      <TimelineBarChip bar={bar} />
    </button>
  );
};

const TimelineBarChip: React.FC<{
  bar: TimelineBar<ActionEntity>;
  /** Fill a positioned parent instead of positioning itself. */
  inline?: boolean;
}> = ({ bar, inline = false }) => {
  const action = bar.action;
  return (
    <span
      className={`tw:z-20 tw:flex tw:h-6 tw:items-center tw:overflow-hidden tw:rounded tw:px-1.5 tw:text-[11px] tw:text-white ${
        inline ? 'tw:w-full' : 'tw:absolute'
      } ${
        bar.overdue ? 'tw:bg-destructive' : actionStatusBarClass(action.status)
      }`}
      style={{
        ...(inline
          ? {}
          : { left: `${bar.left * 100}%`, width: `${bar.width * 100}%` }),
        opacity: bar.isMilestone ? 0.85 : 1,
      }}
      title={`${action.name} · ${action.priority ?? 'Normal'}`}
    >
      <span className="tw:truncate">{action.name}</span>
      <Badge
        variant={actionPriorityBadgeVariant(action.priority)}
        className="tw:ml-1 tw:h-4 tw:px-1.5 tw:text-[0.6rem]"
      >
        {action.priority ?? 'Normal'}
      </Badge>
    </span>
  );
};

/**
 * Tasks as bars across a window. Rows stay one-per-action so a column lines up
 * with the same task in the table. HWCG can opt into roster-position lanes,
 * where bars share a lane per seat and are packed so none overlap.
 */
const ActionsTimeline: React.FC<ActionsTimelineProps> = ({
  scope = INCIDENT,
  incidentIdOverride,
  range,
  onEditAction,
  filteredActions,
  showRosterPositions = false,
  rosterPositions = NO_POSITIONS,
  roster = NO_ROSTER,
}) => {
  const isIncident = scope === INCIDENT;
  const incidentQuery = useIncidentActions(scope, incidentIdOverride);
  const orgQuery = useOrgActions(scope);
  const { data: actions, isLoading } = isIncident ? incidentQuery : orgQuery;

  const activeActions = useMemo(
    () =>
      filteredActions ?? (actions ?? []).filter((action) => !action.archived),
    [actions, filteredActions]
  );

  const window = useMemo(() => timelineWindow(range), [range]);
  const bars = useMemo(
    () => timelineBars(activeActions, window),
    [activeActions, window]
  );
  const lanes = useMemo(
    () =>
      showRosterPositions
        ? timelinePositionLanes(activeActions, window, rosterPositions, roster)
        : [],
    [activeActions, window, showRosterPositions, rosterPositions, roster]
  );
  const hidden = useMemo(
    () => describeHiddenActions(countOutsideWindow(activeActions, window)),
    [activeActions, window]
  );
  const nowAt = useMemo(() => nowFraction(window), [window]);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="tw:space-y-2">
      {hidden ? (
        <p className="tw:mb-0 tw:text-xs tw:text-foreground">{hidden}</p>
      ) : null}
      <div className="tw:overflow-x-auto tw:rounded-md tw:border tw:border-border">
        <div className="tw:relative tw:min-w-[640px]">
          <div className="tw:relative tw:h-8 tw:border-b tw:border-border tw:bg-muted/40">
            {window.ticks.map((tick) => (
              <div
                key={tick.at}
                className="tw:absolute tw:top-0 tw:h-full tw:border-l tw:border-border tw:px-1 tw:text-[11px] tw:text-foreground"
                style={{ left: `${tick.fraction * 100}%` }}
              >
                {tick.label}
              </div>
            ))}
            {nowAt != null ? (
              <div
                className="tw:absolute tw:top-0 tw:z-10 tw:h-full tw:w-px tw:bg-destructive"
                style={{ left: `${nowAt * 100}%` }}
                title="Now"
              />
            ) : null}
          </div>
          {(showRosterPositions ? lanes.length === 0 : bars.length === 0) ? (
            <p className="tw:m-4 tw:text-center tw:text-sm tw:text-foreground">
              Nothing scheduled in this window.
            </p>
          ) : showRosterPositions ? (
            lanes.map((lane) => (
              <div
                key={lane.id}
                data-testid={`timeline-lane-${lane.id}`}
                className="tw:border-b tw:border-border"
              >
                <div className="tw:bg-muted/30 tw:px-2 tw:py-0.5 tw:text-[11px] tw:font-semibold tw:text-foreground">
                  {lane.label} ({lane.count})
                </div>
                {lane.rows.map((row, index) => (
                  <div
                    key={`${lane.id}-${index}`}
                    className="tw:relative tw:h-9 tw:border-b tw:border-border/60"
                  >
                    {row.map((bar) => (
                      <button
                        key={String(bar.action.id)}
                        type="button"
                        id={`action-lane-${lane.id}-${getActionMapId(bar.action)}`}
                        className="tw:absolute tw:top-1.5 tw:z-20 tw:border-0 tw:bg-transparent tw:p-0 tw:text-left"
                        style={{
                          left: `${bar.left * 100}%`,
                          width: `${bar.width * 100}%`,
                        }}
                        onClick={() => onEditAction(bar.action)}
                      >
                        <TimelineBarChip bar={bar} inline />
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            ))
          ) : (
            bars.map((bar) => (
              <TimelineBarRow
                key={String(bar.action.id)}
                bar={bar}
                window={window}
                nowAt={nowAt}
                onEditAction={onEditAction}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ActionsTimeline;
